import React, { useState, useEffect, useRef } from "react";
import { ZoomIn, X } from 'lucide-react';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const Product = ({ product, addToCart, cartItems }) => {
    const [selectedPrice, setSelectedPrice] = useState(product?.Prices[0] || "");
    const [addedToCart, setAddedToCart] = useState(false);
    const [zoomed, setIsZoomed] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [isZoomActive, setIsZoomActive] = useState(false);

    useEffect(() => {
        const isProductInCart = cartItems.some(item => item.ProductID === product.ProductID);
        setAddedToCart(isProductInCart);
    }, [cartItems, product.ProductID]);

    const handleMouseMove = (e) => {
        const { left, top, width, height } = e.target.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;

        setZoomPosition({ x, y });
        setIsZoomActive(true);
    };

    const handleMouseLeave = () => {
        setIsZoomActive(false);
    };

    return (
        <>
            <Card className="overflow-hidden hover:shadow-lg transition-shadow group">
                <CardContent className="p-0">
                    {/* Product Image */}
                    <div 
                        className="relative aspect-square cursor-pointer overflow-hidden rounded-lg p-4"
                        onClick={() => setIsZoomed(true)}
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                            <div className="bg-card rounded-full p-2 shadow-lg">
                                <ZoomIn className="h-6 w-6 text-primary" />
                            </div>
                        </div>
                        <img 
                            src={product?.ThumbnailURL} 
                            alt={product?.ProductName}
                            className="w-full h-full object-cover rounded-lg"
                        />
                    </div>
                    
                    {/* Product Info */}
                    <div className="px-4 pb-2 space-y-3">
                        <h3 
                            className="font-semibold text-lg text-primary line-clamp-2 min-h-[48px]" 
                            title={product?.ProductName}
                        >
                            {product?.ProductName}
                        </h3>
                    </div>
                </CardContent>

                <CardFooter className="p-4 pt-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Price Dropdown */}
                    <Select 
                        value={selectedPrice.QuantityThreshold} 
                        onValueChange={(value) => {
                            const selected = product.Prices.find(p => p.QuantityThreshold === value);
                            setSelectedPrice(selected);
                        }}
                    >
                        <SelectTrigger className="w-[140px]">
                            <SelectValue>
                                <div className="flex items-center gap-1">
                                    <span className="font-semibold">${selectedPrice.QuantityPrice}</span>
                                    <span className="text-muted-foreground">-</span>
                                    <span className="text-sm">{selectedPrice.QuantityThreshold}</span>
                                </div>
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {product.Prices.map((price, index) => (
                                <SelectItem key={index} value={price.QuantityThreshold}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold">${price.QuantityPrice}</span>
                                        <span className="text-muted-foreground">-</span>
                                        <span>{price.QuantityThreshold}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Add to Cart Button */}
                    <Button
                        onClick={() => {
                            addToCart(product, selectedPrice);
                            setAddedToCart(true);
                        }}
                        disabled={addedToCart}
                        variant={addedToCart ? "default" : "secondary"}
                        className={`flex-1 ${addedToCart ? 'bg-[#313131] hover:bg-[#313131] hover:shadow-lg text-white' : ''}`}
                    >
                        {addedToCart ? 'Added' : 'Add to Cart'}
                    </Button>
                </CardFooter>
            </Card>

            {/* Zoom Modal */}
            <Dialog open={zoomed} onOpenChange={setIsZoomed}>
                <DialogContent className="max-w-3xl max-h-[calc(100vh-40px)] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{product?.ProductName}</DialogTitle>
                    </DialogHeader>
                    <div 
                        className="relative w-full flex-1 bg-gray-50 overflow-hidden rounded-lg"
                        style={{ maxHeight: 'calc(100vh - 160px)' }}
                        onMouseMove={handleMouseMove} 
                        onMouseLeave={handleMouseLeave}
                    >
                        <img
                            src={product?.ThumbnailURL}
                            alt={product?.ProductName}
                            className="w-full h-full object-contain transition-transform duration-200"
                            style={{
                                transform: isZoomActive ? "scale(2)" : "scale(1)",
                                transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Product;

